/**
 * Unified entity search hook for @ mention autocomplete
 * Searches across policies, attributes, subjects, and classifications
 */

import { useQueries } from '@tanstack/react-query'
import { get } from '@/lib/api-client'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import type { MentionEntity, MentionEntityType } from '@/types/rich-input'
import type { PolicyDraftListItem } from '@/types/policy'
import type { Attribute } from '@/types/attribute'
import type { ClassificationNode } from '@/types/classification'

const SEARCH_DEBOUNCE_MS = 200
const SEARCH_PAGE_SIZE = 8
const SEARCH_STALE_TIME = 30_000

interface EntitySearchOptions {
  readonly query: string
  readonly enabled: boolean
}

interface EntitySearchResult {
  readonly results: ReadonlyMap<MentionEntityType, readonly MentionEntity[]>
  readonly flatResults: readonly MentionEntity[]
  readonly isLoading: boolean
  readonly isEmpty: boolean
}

/**
 * Response shape for paginated policy search
 */
interface PolicySearchResponse {
  readonly data: readonly PolicyDraftListItem[]
}

/**
 * Response shape for paginated attribute search
 */
interface AttributeSearchResponse {
  readonly data: readonly Attribute[]
}

/**
 * Subject entity from Identity Store API
 */
interface SubjectEntry {
  readonly id: string
  readonly displayName: string
  readonly email?: string
  readonly subjectType?: string
}

/**
 * Response shape for paginated subject search
 */
interface SubjectSearchResponse {
  readonly data: readonly SubjectEntry[]
}

function mapPolicyToEntity(policy: PolicyDraftListItem): MentionEntity {
  return {
    id: policy.id,
    entityType: 'policy',
    displayName: policy.name,
    description: policy.description ?? undefined,
    meta: { status: policy.status },
  }
}

function mapAttributeToEntity(attr: Attribute): MentionEntity {
  return {
    id: attr.id,
    entityType: 'attribute',
    displayName: attr.displayName,
    description: attr.description ?? undefined,
    meta: { category: attr.category, dataType: attr.dataType },
  }
}

function mapSubjectToEntity(subject: SubjectEntry): MentionEntity {
  return {
    id: subject.id,
    entityType: 'subject',
    displayName: subject.displayName,
    description: subject.email ?? undefined,
    meta: { subjectType: subject.subjectType },
  }
}

function mapClassificationToEntity(node: ClassificationNode): MentionEntity {
  return {
    id: node.id,
    entityType: 'classification',
    displayName: node.displayName,
    description: node.description ?? undefined,
    meta: { level: node.level },
  }
}

/**
 * Flatten a classification tree, optionally filtered by query
 */
function flattenAndFilterTree(
  nodes: readonly ClassificationNode[],
  query: string
): readonly ClassificationNode[] {
  const lower = query.toLowerCase()
  const results: ClassificationNode[] = []

  function walk(node: ClassificationNode) {
    if (
      node.displayName.toLowerCase().includes(lower) ||
      node.name.toLowerCase().includes(lower) ||
      (node.description?.toLowerCase().includes(lower) ?? false)
    ) {
      results.push(node)
    }
    for (const child of node.children) {
      walk(child)
    }
  }

  for (const root of nodes) {
    walk(root)
  }

  return results.slice(0, SEARCH_PAGE_SIZE)
}

/**
 * Search across all entity types in parallel using React Query.
 *
 * - Policies: `GET /policy-drafts?search=X&pageSize=8`
 * - Attributes: `GET /attributes?search=X&pageSize=8`
 * - Subjects: `GET /subjects?search=X&pageSize=8`
 * - Classifications: client-side filter on cached `GET /classifications/tree`
 */
export function useEntitySearch({
  query,
  enabled,
}: EntitySearchOptions): EntitySearchResult {
  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS)
  const shouldFetch = enabled && debouncedQuery.trim().length > 0

  const queryResults = useQueries({
    queries: [
      // Policies
      {
        queryKey: ['entity-search', 'policy', debouncedQuery],
        queryFn: () =>
          get<PolicySearchResponse>(
            `/policy-drafts?search=${encodeURIComponent(debouncedQuery)}&pageSize=${SEARCH_PAGE_SIZE}`
          ),
        enabled: shouldFetch,
        staleTime: SEARCH_STALE_TIME,
        placeholderData: (prev: PolicySearchResponse | undefined) => prev,
      },
      // Attributes
      {
        queryKey: ['entity-search', 'attribute', debouncedQuery],
        queryFn: () =>
          get<AttributeSearchResponse>(
            `/attributes?search=${encodeURIComponent(debouncedQuery)}&pageSize=${SEARCH_PAGE_SIZE}`
          ),
        enabled: shouldFetch,
        staleTime: SEARCH_STALE_TIME,
        placeholderData: (prev: AttributeSearchResponse | undefined) => prev,
      },
      // Subjects
      {
        queryKey: ['entity-search', 'subject', debouncedQuery],
        queryFn: () =>
          get<SubjectSearchResponse>(
            `/subjects?search=${encodeURIComponent(debouncedQuery)}&pageSize=${SEARCH_PAGE_SIZE}`
          ),
        enabled: shouldFetch,
        staleTime: SEARCH_STALE_TIME,
        placeholderData: (prev: SubjectSearchResponse | undefined) => prev,
      },
      // Classifications (client-side filter on tree)
      {
        queryKey: ['entity-search', 'classification', debouncedQuery],
        queryFn: () => get<ClassificationNode[]>('/classifications/tree'),
        enabled: shouldFetch,
        staleTime: 10 * 60 * 1000, // tree changes rarely
        placeholderData: (prev: ClassificationNode[] | undefined) => prev,
      },
    ],
  })

  const [policiesQ, attributesQ, subjectsQ, classificationsQ] = queryResults

  const isLoading = queryResults.some((q) => q.isLoading)

  // Build results map
  const resultsMap = new Map<MentionEntityType, readonly MentionEntity[]>()

  const policyEntities = (policiesQ.data?.data ?? []).map(mapPolicyToEntity)
  if (policyEntities.length > 0) {
    resultsMap.set('policy', policyEntities)
  }

  const attributeEntities = (attributesQ.data?.data ?? []).map(
    mapAttributeToEntity
  )
  if (attributeEntities.length > 0) {
    resultsMap.set('attribute', attributeEntities)
  }

  const subjectEntities = (subjectsQ.data?.data ?? []).map(mapSubjectToEntity)
  if (subjectEntities.length > 0) {
    resultsMap.set('subject', subjectEntities)
  }

  const rawClassifications = classificationsQ.data
  const classificationNodes =
    rawClassifications && Array.isArray(rawClassifications)
      ? flattenAndFilterTree(rawClassifications, debouncedQuery)
      : []
  const classificationEntities = classificationNodes.map(
    mapClassificationToEntity
  )
  if (classificationEntities.length > 0) {
    resultsMap.set('classification', classificationEntities)
  }

  // Flat list in category order
  const flatResults: readonly MentionEntity[] = [
    ...policyEntities,
    ...attributeEntities,
    ...subjectEntities,
    ...classificationEntities,
  ]

  const isEmpty = !isLoading && flatResults.length === 0 && shouldFetch

  return {
    results: resultsMap,
    flatResults,
    isLoading,
    isEmpty,
  }
}
